import { useState } from "react";
import { Mail, Phone, MapPin, Clock, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import SEO from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";

const Contact = () => {
  const [contactSent, setContactSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);

    const { error } = await supabase.functions.invoke("send-form-email", {
      body: {
        formType: "contact",
        name: formData.get("contactName"),
        email: formData.get("contactEmail"),
        message: formData.get("contactMessage"),
      },
    });

    setLoading(false);

    if (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Contact form error:", error);
      return;
    }

    setContactSent(true);
    toast.success("Message sent! We'll get back to you soon.");
  };

  return (
    <Layout>
      <SEO title="Contact · Soft Chaos Studio" description="Get in touch with Soft Chaos Studio for custom gift enquiries, commissions & quotes." canonical="/contact" />
      <section className="container py-16">
        <h1 className="text-4xl font-serif font-semibold text-center mb-3">Get in Touch</h1>
        <p className="text-center text-muted-foreground mb-12">We'd love to hear from you. Drop us a message below.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Contact form */}
          <div>
            <h2 className="font-serif text-xl font-semibold mb-5">Send a Message</h2>
            {contactSent ? (
              <div className="text-center py-12 bg-sage rounded-lg animate-fade-in">
                <p className="font-serif text-lg font-semibold">Thanks for your message!</p>
                <p className="text-sm text-muted-foreground mt-2">We'll reply as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="contactName">Name *</Label>
                  <Input id="contactName" name="contactName" required placeholder="Your name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Email *</Label>
                  <Input id="contactEmail" name="contactEmail" type="email" required placeholder="your@email.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactMessage">Message *</Label>
                  <Textarea id="contactMessage" name="contactMessage" required rows={5} placeholder="How can we help?" />
                </div>
                <Button type="submit" disabled={loading}>
                  {loading ? "Sending..." : "Send Message"}
                </Button>
              </form>
            )}
          </div>

          {/* Contact info */}
          <div>
            <h2 className="font-serif text-xl font-semibold mb-5">Contact Info</h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-muted-foreground"><a href="mailto:softchaos.uk@gmail.com" className="hover:text-primary transition-colors">softchaos.uk@gmail.com</a></p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Phone</p>
                  <p className="text-muted-foreground"><a href="tel:+447414896899" className="hover:text-primary transition-colors">+447414896899</a></p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Location</p>
                  <p className="text-muted-foreground">United Kingdom</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Business Hours</p>
                  <p className="text-muted-foreground">24/7</p>
                  <p className="text-muted-foreground">Except public holidays (due to delivery services)</p>
                </div>
              </li>
            </ul>

            <div className="mt-6">
              <p className="font-medium text-sm mb-2">Follow Us</p>
              <a
                href="https://www.facebook.com/profile.php?id=61586838426531"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-sage flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
            </div>

            {/* Map placeholder */}
            <div className="mt-8 rounded-lg overflow-hidden h-48">
              <iframe
                title="Norwich, UK Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d77643.09365040505!2d1.2186!3d52.6309!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d9e282abe5645b%3A0x9a523eb78092b83a!2sNorwich!5e0!3m2!1sen!2suk!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
